import { Html } from '@react-three/drei'
import { useFrame } from '@react-three/fiber'
import { type CSSProperties, useRef } from 'react'
import { HITS_TO_STUN } from '../game/combat'
import { getRacerIdentity } from '../game/racer-config'
import type { GameRuntime } from '../game/runtime'
import { formatOrdinal } from '../ui/race-format'

interface RacerNameplateProps {
  runtime: GameRuntime
  vehicleId: string
}

export function RacerNameplate({ runtime, vehicleId }: RacerNameplateProps) {
  const rankRef = useRef<HTMLSpanElement>(null)
  const statusRef = useRef<HTMLElement>(null)
  const identity = getRacerIdentity(vehicleId)
  const initialVehicle = runtime.getState().vehicles.find((candidate) => candidate.id === vehicleId)
  const displayName = initialVehicle?.displayName ?? identity.name
  const initialRank = Math.max(0, runtime.getState().ranking.indexOf(vehicleId)) + 1

  useFrame(() => {
    const state = runtime.getState()
    const vehicle = state.vehicles.find((candidate) => candidate.id === vehicleId)
    if (!vehicle) return
    if (rankRef.current) rankRef.current.textContent = formatOrdinal(state.ranking.indexOf(vehicleId) + 1)
    if (statusRef.current) {
      statusRef.current.textContent =
        vehicle.driverStunTimer > 0
          ? 'DRIVER HIT'
          : vehicle.reloadTimer > 0
            ? 'RELOADING'
            : vehicle.driverHitCount > 0
              ? `HIT ${vehicle.driverHitCount}/${HITS_TO_STUN}`
              : ''
    }
  })

  return (
    <Html position={[0, 3.25, 0]} center distanceFactor={11} zIndexRange={[12, 0]}>
      <div
        className={
          vehicleId === runtime.getState().race.playerId ? 'racer-nameplate racer-nameplate--player' : 'racer-nameplate'
        }
        data-testid="racer-nameplate"
        data-vehicle-id={vehicleId}
        style={{ '--racer-accent': identity.accent } as CSSProperties}
      >
        <span ref={rankRef}>{formatOrdinal(initialRank)}</span>
        <strong>{displayName}</strong>
        <small ref={statusRef} />
      </div>
    </Html>
  )
}
